// B5 — the Defined x Used quadrant (PRD 16 §5): one panel that sizes both
// failure modes at once.
//
// EXCEPT THAT ONE OF THEM CANNOT BE MEASURED, AND THIS PANEL SAYS SO.
//
// PRD 16 names two corners: upper-left (defined, unused) = glossary bloat,
// delete it; lower-right (used, undefined) = the actual gap, write it. The
// first is exact. **The second, as literally worded, is invisible to this
// product**: a hover fires only on a `<mark>`, and a mark exists only where a
// definition exists, so a reader stalling on a word the glossary never covered
// emits nothing at all.
//
// So the panel renders three measured corners and one declared hole. The hole is
// stated on the panel, not buried in a tooltip, because it is the corner a buyer
// would act on by writing content — an estimate there would be the most
// damaging fabrication on this screen. `is_singleton` is offered beside it as
// the nearest honest neighbour and is labelled as what it is: a corpus-TEXT
// signal found by reading the documents, never a count of readers.
//
// Two more things that must not be tidied:
//
//  1. **An unopened term on an unread page is `not_measured`, not bloat.**
//     Without that floor a freshly published document reports its whole glossary
//     as dead on day one — wrong, and at the exact moment a customer is looking.
//  2. **`unused_share` is null, never 0%, below the floor.** 0% claims a clean
//     glossary; `null` says we do not know yet.

import { Card, EmptyState, MeterBar } from "../../ui";
import type { DefinedUsedQuadrant } from "./api";
import { formatCount, formatShare } from "./format";
import {
  NOT_MEASURED,
  QUADRANT_BLOAT_LABEL,
  QUADRANT_BLOAT_WHY,
  QUADRANT_EMPTY_BODY,
  QUADRANT_EMPTY_HEADLINE,
  QUADRANT_LOST_LABEL,
  QUADRANT_LOST_WHY,
  QUADRANT_NO_VERDICT_WHY,
  QUADRANT_SINGLETON_LABEL,
  QUADRANT_SINGLETON_LEAD,
  QUADRANT_SUBTITLE,
  QUADRANT_TITLE,
  QUADRANT_UNMEASURABLE_HEADLINE,
  QUADRANT_UNPUBLISHED_BODY,
  QUADRANT_UNPUBLISHED_HEADLINE,
  QUADRANT_WORKING_LABEL,
  SESSIONS_LABEL,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 6;

export function DefinedUsed({ report }: { report: DefinedUsedQuadrant }) {
  const measured = report.unused_share !== null;
  // The work-list is the two corners a docs team acts on. `defined_used` rows
  // are the healthy case and belong in the counts, not in a list of five.
  const worklist = report.terms
    .filter(
      (t) => t.quadrant === "defined_unused" || t.quadrant === "used_undefined",
    )
    .slice(0, MAX_ROWS);
  const unjudged = report.terms.find((t) => t.quadrant === "not_measured");
  const where = report.doc_title ?? report.doc_id;

  return (
    <Card
      header={QUADRANT_TITLE}
      footer={
        <span className="type-bodyS">
          {report.doc_id === null
            ? QUADRANT_SUBTITLE
            : `${formatCount(report.terms_defined)} terms defined on ${where} · ${formatCount(report.doc_sessions)} ${SESSIONS_LABEL}`}
        </span>
      }
    >
      {report.doc_id === null ? (
        <EmptyState
          headline={QUADRANT_EMPTY_HEADLINE}
          body={QUADRANT_EMPTY_BODY}
        />
      ) : !report.glossary_published ? (
        <EmptyState
          headline={QUADRANT_UNPUBLISHED_HEADLINE}
          body={QUADRANT_UNPUBLISHED_BODY}
        />
      ) : (
        <>
          <ul
            className="maisight-analytics-quadrant"
            aria-label="Defined against opened"
          >
            <li className="maisight-analytics-quadrant-cell">
              <span className="maisight-analytics-quadrant-head type-numSm">
                {QUADRANT_BLOAT_LABEL}
              </span>
              <span
                className="maisight-analytics-metric type-numSm"
                title={measured ? QUADRANT_BLOAT_WHY : undefined}
                data-measured={measured ? "yes" : "no"}
              >
                {/* `formatShare(null)` is NOT_MEASURED, never "0%" — the whole
                  *  point of the traffic floor. */}
                {measured
                  ? `${formatCount(report.defined_unused)} · ${formatShare(report.unused_share)}`
                  : NOT_MEASURED}
              </span>
              <MeterBar
                value={measured ? report.defined_unused : 0}
                max={Math.max(report.terms_defined, 1)}
                tone={measured && report.defined_unused > 0 ? "flagged" : "nominal"}
                label={`${report.defined_unused} of ${report.terms_defined} defined terms were never opened`}
              />
              <p className="maisight-analytics-quadrant-meta type-bodyS">
                {measured
                  ? `of the ${formatCount(report.terms_defined)} terms this page defines`
                  : `${QUADRANT_NO_VERDICT_WHY} ${unjudged?.not_measured_reason ?? ""}`}
              </p>
            </li>

            <li className="maisight-analytics-quadrant-cell">
              <span className="maisight-analytics-quadrant-head type-numSm">
                {QUADRANT_WORKING_LABEL}
              </span>
              <span className="type-numSm maisight-analytics-term">
                {formatCount(report.defined_used)}
              </span>
              <MeterBar
                value={report.defined_used}
                max={Math.max(report.terms_defined, 1)}
                tone="nominal"
                label={`${report.defined_used} of ${report.terms_defined} defined terms were opened`}
              />
              <p className="maisight-analytics-quadrant-meta type-bodyS">
                {`of the ${formatCount(report.terms_defined)} terms this page defines`}
              </p>
            </li>

            <li className="maisight-analytics-quadrant-cell">
              <span className="maisight-analytics-quadrant-head type-numSm">
                {QUADRANT_LOST_LABEL}
              </span>
              <span
                className="type-numSm maisight-analytics-term"
                title={QUADRANT_LOST_WHY}
              >
                {formatCount(report.used_undefined)}
              </span>
              <MeterBar
                value={report.used_undefined}
                max={Math.max(report.terms_defined, 1)}
                tone={report.used_undefined > 0 ? "flagged" : "nominal"}
                label={`${report.used_undefined} terms visits opened are no longer defined`}
              />
              <p className="maisight-analytics-quadrant-meta type-bodyS">
                {QUADRANT_LOST_WHY}
              </p>
            </li>
          </ul>

          {/* The corner that does not exist. On the panel, in full, because
            *  a buyer reading three populated corners will assume the fourth
            *  was empty rather than absent. */}
          <div className="maisight-analytics-quadrant-hole">
            <p className="maisight-analytics-quadrant-hole-head type-bodyS">
              {QUADRANT_UNMEASURABLE_HEADLINE}
            </p>
            <p className="maisight-analytics-quadrant-meta type-bodyS">
              {report.undefined_demand_reason}
            </p>
            <p className="maisight-analytics-quadrant-meta type-bodyS">
              {`${QUADRANT_SINGLETON_LEAD} ${formatCount(report.singletons_defined)} ${QUADRANT_SINGLETON_LABEL}.`}
            </p>
          </div>

          {worklist.length > 0 ? (
            <ul
              className="maisight-analytics-chips"
              aria-label="Terms to review"
            >
              {worklist.map((term) => (
                <li
                  key={term.term_id}
                  className="maisight-analytics-quadrant-chip type-bodyS"
                  data-quadrant={term.quadrant}
                >
                  {term.term ?? term.term_id}
                  {term.quadrant === "used_undefined"
                    ? ` · ${formatCount(term.hover_count)} opened`
                    : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Card>
  );
}
